import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useId, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { isAcclaimedContest, translate } from "@sequentech/ui-core";
import { Trans, useTranslation } from "react-i18next";
import { computeSlateChoices, SlateOverMaximumError, slateRemovesChoices, } from "../../services/SlateChoices";
import { getSlateName } from "../../services/Slates";
import { applySlateSelection, selectBallotSelectionByElectionId, } from "../../store/ballotSelections/ballotSelectionsSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { SlateChangesDialog } from "./SlateChangesDialog";
import { ESlateDetailsAction, SlateDetailsDialog } from "./SlateDetailsDialog";
export var SlateApplyAction = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h;
    var ballotStyle = _a.ballotStyle, slate = _a.slate, onApplied = _a.onApplied, _j = _a.isFullySelected, isFullySelected = _j === void 0 ? false : _j, onEditSelections = _a.onEditSelections;
    var _k = useTranslation(), t = _k.t, i18n = _k.i18n;
    var dispatch = useAppDispatch();
    var selection = useAppSelector(selectBallotSelectionByElectionId(ballotStyle.election_id));
    var _l = useState(), pending = _l[0], setPending = _l[1];
    var _m = useState(false), isDetailsOpen = _m[0], setIsDetailsOpen = _m[1];
    var unavailableId = useId();
    var slateName = getSlateName(slate, i18n.language, (_d = (_c = (_b = ballotStyle.ballot_eml.election_presentation) === null || _b === void 0 ? void 0 : _b.language_conf) === null || _c === void 0 ? void 0 : _c.default_language_code) !== null && _d !== void 0 ? _d : (_f = (_e = ballotStyle.ballot_eml.election_event_presentation) === null || _e === void 0 ? void 0 : _e.language_conf) === null || _f === void 0 ? void 0 : _f.default_language_code);
    var _o = useMemo(function () {
        if (!selection) {
            return {};
        }
        try {
            return { choices: computeSlateChoices(slate, ballotStyle.ballot_eml.contests, selection) };
        }
        catch (error) {
            console.log("Error computing slate choices: ".concat(error));
            return { problem: error };
        }
    }, [slate, ballotStyle, selection]), choices = _o.choices, problem = _o.problem;
    var unavailableReason = useMemo(function () {
        var _a;
        if (!problem) {
            return null;
        }
        if (problem instanceof SlateOverMaximumError) {
            var _b = problem.slateContest, contest = _b.contest, candidates = _b.candidates;
            return t("slates.apply.overMaximum", {
                slate: slateName,
                contest: (_a = translate(contest, "name", i18n.language)) !== null && _a !== void 0 ? _a : contest.id,
                candidates: candidates.length,
                max: contest.max_votes,
            });
        }
        return t("slates.apply.unavailable", { slate: slateName });
    }, [problem, slateName, t, i18n.language]);
    var apply = function () {
        dispatch(applySlateSelection({ ballotStyle: ballotStyle, slate: slate }));
        onApplied === null || onApplied === void 0 ? void 0 : onApplied(slate);
    };
    var handleChoose = function () {
        if (!choices) {
            return;
        }
        if (slateRemovesChoices(choices)) {
            setPending(choices);
        }
        else {
            apply();
        }
    };
    var handleClose = function (confirmed) {
        setPending(undefined);
        if (confirmed) {
            apply();
        }
    };
    var affected = ((_g = pending === null || pending === void 0 ? void 0 : pending.changes) !== null && _g !== void 0 ? _g : []).filter(function (change) { return change.removed.length > 0; });
    var onlyAffected = affected[0];
    var onlyAffectedContest = affected.length === 1
        ? ballotStyle.ballot_eml.contests.find(function (contest) { return contest.id === onlyAffected.contestId; })
        : undefined;
    var leavesOfficesBlank = ballotStyle.ballot_eml.contests.some(function (contest) {
        return !isAcclaimedContest(contest) &&
            !slate.contests.some(function (entry) { return entry.contest.id === contest.id; });
    });
    return (_jsxs(Box, { className: "slate-apply", sx: {
            "display": "flex",
            "flexDirection": { xs: "row", sm: "column" },
            "flexWrap": "wrap",
            "gap": { xs: "8px", sm: "3px" },
            "& .slate-apply-button": { flex: { xs: "1.1 1 0", sm: "none" } },
            "& .slate-view-button": {
                flex: { xs: "1 1 0", sm: "none" },
                minHeight: "44px",
                borderColor: "transparent",
                background: "transparent",
            },
            "& .slate-apply-unavailable": { flexBasis: "100%" },
        }, children: [_jsx(Button, { className: "slate-apply-button", onClick: handleChoose, disabled: !choices, "aria-label": t("slates.apply.buttonLabel", { slate: slateName }), "aria-describedby": unavailableReason ? unavailableId : undefined, children: t("slates.apply.button") }), _jsx(Button, { className: "slate-view-button", variant: "secondary", onClick: function () { return setIsDetailsOpen(true); }, "aria-label": t("slates.view.buttonLabel", { slate: slateName }), children: t("slates.view.button") }), unavailableReason && (_jsx(Typography, { className: "slate-apply-unavailable", id: unavailableId, variant: "body2", children: unavailableReason })), _jsx(SlateDetailsDialog, { open: isDetailsOpen, slate: slate, slateName: slateName, action: isFullySelected
                    ? ESlateDetailsAction.EDIT
                    : choices
                        ? ESlateDetailsAction.SELECT
                        : ESlateDetailsAction.UNAVAILABLE, handleClose: function (confirmed) {
                    setIsDetailsOpen(false);
                    if (!confirmed) {
                        return;
                    }
                    if (isFullySelected) {
                        onEditSelections === null || onEditSelections === void 0 ? void 0 : onEditSelections(slate);
                    }
                    else {
                        handleChoose();
                    }
                } }), _jsx(SlateChangesDialog, { className: "slate-replace-dialog", pending: pending, current: selection, contests: ballotStyle.ballot_eml.contests, handleClose: handleClose, title: t("slates.apply.replaceDialog.title"), content: _jsx(Trans, { i18nKey: "slates.apply.replaceDialog.content", values: { slate: slateName }, count: affected.length, components: { strong: _jsx("strong", { className: "slate-replace-slate" }) } }), afterLabel: slateName, note: leavesOfficesBlank
                    ? t("slates.apply.replaceDialog.blankNote", { slate: slateName })
                    : undefined, ok: onlyAffectedContest
                    ? t("slates.apply.replaceDialog.okOffice", {
                        office: ((_h = translate(onlyAffectedContest, "name", i18n.language)) !== null && _h !== void 0 ? _h : "").toLocaleLowerCase(),
                    })
                    : t("slates.apply.replaceDialog.ok"), cancel: t("slates.apply.replaceDialog.cancel") })] }));
};
